/**
 * View-as session manager.
 *
 * Owns all localStorage state for admin "view as" mode so the feature is
 * isolated to a single file. cloudApi only needs to expose viewAsUserId for
 * request-level header injection; everything else lives here.
 *
 * Usage:
 *   viewAs.enter(targetUser)  → switch into view-as mode
 *   viewAs.exit()             → restore admin's own context
 *   viewAs.restore()          → re-hydrate after a page refresh or new tab
 *   viewAs.clear()            → clean up without workspace restoration (e.g. on 401)
 */

import cloudApi from './cloudApi';

const USER_DATA_KEY = 'cloudViewAsUserData';
const ADMIN_WORKSPACE_KEY = 'cloudAdminWorkspaceId';

export const viewAs = {
  /**
   * Enter view-as mode for the given target user.
   * Returns the target's workspaces so the caller can update React state.
   */
  async enter(targetUser) {
    if (cloudApi.workspaceId) {
      localStorage.setItem(ADMIN_WORKSPACE_KEY, cloudApi.workspaceId);
    }

    // Pass viewAsUserId per-request so concurrent requests don't pick up the header
    const workspaces = await cloudApi.getWorkspaces({ viewAsUserId: targetUser.id });

    localStorage.setItem(USER_DATA_KEY, JSON.stringify(targetUser));

    const firstWs = workspaces[0] ?? null;
    if (firstWs?.id) await cloudApi.setWorkspaceId(firstWs.id);

    return { workspaces, currentWorkspace: firstWs };
  },

  /**
   * Exit view-as mode and restore the admin's original workspace.
   */
  async exit() {
    const adminWsId = localStorage.getItem(ADMIN_WORKSPACE_KEY);
    this.clear();
    if (adminWsId) await cloudApi.setWorkspaceId(adminWsId);
  },

  /**
   * Read persisted view-as state (used on page refresh / new tab).
   * Returns the stored target user object, or null if not in view-as mode.
   */
  restore() {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  /**
   * Remove all view-as state without restoring the admin's workspace.
   * Use this on logout or token expiry where workspace restoration is irrelevant.
   */
  clear() {
    cloudApi.viewAsUserId = null;
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(ADMIN_WORKSPACE_KEY);
  },
};
